// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { xIsAuthenticated } from "@steiner/App/Store/Selectors/SessionSelectors";
import Menu, { MappedLink, MenuPresentationProps } from "@steiner/MobiusOverrides/Menu";
import NavArrowDown from "@steiner/XDL/Icons/NavArrowDown";
import { truncateOrBreakLineText, xClassNames } from "@steiner/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    primaryLinks = "primaryLinks",
    secondaryLinks = "secondaryLinks",
    isCompactHeader = "isCompactHeader",
    customCSS = "customCSS",
}

// XNG-Changes
const mapLink = (link: HeaderLinkModel, state: CustomAppState): MappedLink | undefined => {
    const {
        fields: { linkType, destination, openInNewWindow, overriddenTitle, maxDepth },
    } = link;

    const { type, value } = destination;
    const depthToLoad = maxDepth || 3;

    let mappedLink: MappedLink | undefined;

    if (type === "Url") {
        mappedLink = {
            url: value,
            title: overriddenTitle,
        };
    } else if (type === "Page") {
        const readonlyPageLink = getPageLinkByNodeId(state, value);
        if (readonlyPageLink) {
            mappedLink = { ...readonlyPageLink };
            if (overriddenTitle) {
                mappedLink.title = overriddenTitle;
            }
        }
    }

    if (!mappedLink) {
        return;
    }

    mappedLink.openInNewWindow = openInNewWindow;
    mappedLink.maxDepth = depthToLoad;

    if (linkType === "CascadingMenu") {
        mappedLink.childrenType = linkType;
    }

    return mappedLink;
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const {
        fields: { primaryLinks = [], secondaryLinks = [] },
    } = ownProps;

    const mappedPrimaryLinks = primaryLinks
        .map(link => mapLink(link, state))
        .filter((link): link is MappedLink => Boolean(link));

    const mappedSecondaryLinks = secondaryLinks
        .map(link => mapLink(link, state))
        .filter((link): link is MappedLink => Boolean(link));

    return {
        mappedPrimaryLinks,
        mappedSecondaryLinks,
        financialAccess: state.context.session?.properties?.financialAccess === "true",
        billTrustLink: state.context.session?.properties?.billTrustLink,
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.primaryLinks]: HeaderLinkModel[];
        [fields.secondaryLinks]: HeaderLinkModel[];
        [fields.isCompactHeader]: boolean;
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

// XNG-Changes
export interface HeaderLinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        linkType: "Link" | "CascadingMenu";
        maxDepth: number;
    };
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    /**
     * XNG-Changes
     */
    container?: InjectableCss;
    firstNameWrapper?: InjectableCss;
    firstName?: TypographyPresentationProps;
    cascadingMenu?: MenuPresentationProps;
    itemWrapper?: InjectableCss;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            text-align: right;
            padding: 0 0 23px 0;
            margin-right: -9px;
        `,
    },
    link: {
        typographyProps: {
            variant: "headerTertiary",
            css: css`
                margin: 0 9px 0 9px;
                display: inline-block;
                &&& {
                    color: ${getColor("text.main")};
                }
            `,
        },
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

export const HeaderLinkList: React.FC<Props> = ({
    fields,
    extendedStyles,
    mappedPrimaryLinks,
    mappedSecondaryLinks,
    financialAccess,
    billTrustLink,
}) => {
    const { firstName, isAuthenticated } = useAppSelector(state => ({
        firstName: getSession(state).firstName?.split(" ")?.[0],
        isAuthenticated: xIsAuthenticated(state),
    }));

    const { links: primaryLinks, hasMissingCategories } = useGetLinks(fields.primaryLinks, o => o.fields.destination);
    const { links: secondaryLinks } = useGetLinks(fields.secondaryLinks, o => o.fields.destination);

    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    if (hasMissingCategories) {
        return null;
    }

    if (primaryLinks.length < 1 && secondaryLinks.length < 1 && !isAuthenticated) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${styles.container.css}
            ${fields.customCSS}
        `,
    };

    // xng-change
    const isBillTrustLink = (link: MappedLink) => {
        return link.title?.toLowerCase()?.includes("bill") || link.url?.includes("billtrust");
    };

    // XNG-Changes
    return (
        <StyledWrapper {...customCssWrapper} data-test-selector="HeaderLinkList">
            {mappedPrimaryLinks.length > 0 && (
                <StyledWrapper className="list-wrapper primary" {...styles.headerLinkListWrapper}>
                    {mappedPrimaryLinks.map((link, index) => {
                        // xng-change
                        if (!link.url || (isBillTrustLink(link) && !financialAccess)) {
                            return null;
                        }
                        const isCascading = link.childrenType === "CascadingMenu";
                        const downIcon = <StyledIcon src={NavArrowDown} size={12} />;
                        const menuLink = (
                            <Link
                                key={`${link.url}.${link.title}`}
                                className="list-link"
                                {...styles.link}
                                // xng-change
                                href={isBillTrustLink(link) ? billTrustLink : link.url}
                                target={link.openInNewWindow ? "_blank" : ""}
                                aria-label={link.title}
                            >
                                {link.title}
                                {isCascading && downIcon}
                            </Link>
                        );
                        if (isCascading) {
                            return (
                                <StyledWrapper className="item-wrapper" {...styles.itemWrapper} key={index}>
                                    <Menu
                                        referenceIndex={index}
                                        className="cascading-menu"
                                        data-test-selector={`headerLinkItem_${index}`}
                                        descriptionId={`${link.title}_${index}`}
                                        menuItems={link.children}
                                        menuTrigger={menuLink}
                                        menuVariant="primary"
                                        // {...menuProps}
                                        {...styles.cascadingMenu}
                                    />
                                </StyledWrapper>
                            );
                        }
                        if (index === 0) {
                            return (
                                <React.Fragment key={`Fragment_${link.url}.${link.title}`}>
                                    <Menu
                                        key={`rootMenu_${link.url}.${link.title}`}
                                        className="small-screen"
                                        menuItems={secondaryLinks.map((link, index) => ({
                                            title:
                                                fields.secondaryLinks[index].fields.overriddenTitle || link.title || "",
                                            url: link.url,
                                            childrenType: "CascadingMenu",
                                        }))}
                                        offset={{ mainAxis: 4 }}
                                        placement="bottom"
                                        menuTrigger={
                                            <Link
                                                className="list-link small-screen"
                                                key={`${link.url}.${link.title}`}
                                                href={link.url}
                                                icon={{
                                                    iconProps: {
                                                        src: NavArrowDown,
                                                        size: 11,
                                                    },
                                                    position: "right",
                                                }}
                                                target={link.openInNewWindow ? "_blank" : ""}
                                                {...styles.link}
                                            >
                                                {fields.primaryLinks[index].fields.overriddenTitle || link.title}
                                            </Link>
                                        }
                                    />
                                    <Link
                                        className="list-link default-screen"
                                        key={`${link.url}.${link.title}`}
                                        href={link.url}
                                        target={fields.primaryLinks[index].fields.openInNewWindow ? "_blank" : ""}
                                        {...styles.link}
                                    >
                                        {link.title}
                                    </Link>
                                </React.Fragment>
                            );
                        }

                        return menuLink;
                    })}
                </StyledWrapper>
            )}
            {mappedSecondaryLinks.length > 0 && (
                <StyledWrapper
                    className={xClassNames.append(
                        [primaryLinks.length > 0 && "separate-line", "default-screen"],
                        "list-wrapper secondary",
                    )}
                    {...styles.headerLinkListWrapper}
                >
                    {mappedSecondaryLinks.map((link, index) => {
                        // xng-change
                        if (!link.url || (isBillTrustLink(link) && !financialAccess)) {
                            return null;
                        }
                        const isCascading = link.childrenType === "CascadingMenu";
                        const downIcon = <StyledIcon src={NavArrowDown} size={12} />;
                        const menuLink = (
                            <Link
                                key={`${link.url}.${link.title}`}
                                className="list-link"
                                {...styles.link}
                                // xng-change
                                href={isBillTrustLink(link) ? billTrustLink : link.url}
                                target={link.openInNewWindow ? "_blank" : ""}
                                aria-label={link.title}
                            >
                                {link.title}
                                {isCascading && downIcon}
                            </Link>
                        );
                        if (isCascading) {
                            return (
                                <StyledWrapper className="item-wrapper" {...styles.itemWrapper} key={index}>
                                    <Menu
                                        referenceIndex={index}
                                        className="cascading-menu"
                                        data-test-selector={`headerLinkItem_${index}`}
                                        descriptionId={`${link.title}_${index}`}
                                        menuItems={link.children}
                                        menuTrigger={menuLink}
                                        menuVariant="primary"
                                        // {...menuProps}
                                        {...styles.cascadingMenu}
                                    />
                                </StyledWrapper>
                            );
                        }
                        return menuLink;
                    })}
                </StyledWrapper>
            )}
            {!fields.isCompactHeader && isAuthenticated && firstName && (
                <StyledWrapper
                    data-test-selector="UserName"
                    className={xClassNames.append(secondaryLinks.length > 0 && "separate-line")}
                    {...styles.headerLinkListWrapper}
                >
                    <Typography {...styles.firstName} title={translate("Hi, {0}", firstName)}>
                        {translate(
                            "Hi, {0}",
                            truncateOrBreakLineText({
                                text: firstName,
                                truncateAfter: 7,
                            }) as string,
                        )}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const createListField = (fieldName: string): FieldDefinition => {
    return {
        name: fieldName,
        editorTemplate: "ListField",
        getDisplay: (item: HasFields, state) => {
            const {
                destination: { type, value },
                overriddenTitle,
            } = item.fields;
            if (overriddenTitle) {
                return overriddenTitle;
            }
            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return link?.title;
            }
            if (type === "Category") {
                let displayValue = "";
                const categories = (state as any).pageEditor.categories;
                if (!categories) {
                    displayValue = "Loading...";
                } else {
                    const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                    if (matchingCategories.length === 0) {
                        displayValue = "Unknown";
                    } else {
                        displayValue = matchingCategories[0].shortDescription;
                    }
                }

                return displayValue;
            }
            if (type === "Url") {
                return value;
            }

            return value;
        },
        defaultValue: [],
        fieldType: "General",
        fieldDefinitions: [
            {
                name: "destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                allowCategories: _ => false,
            },
            {
                name: "overriddenTitle",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "openInNewWindow",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            // XNG-Changes
            {
                name: "linkType",
                editorTemplate: "RadioButtonsField",
                defaultValue: "Link",
                isRequired: true,
                options: [
                    {
                        displayName: "Link",
                        value: "Link",
                    },
                    {
                        displayName: "Cascading Menu",
                        value: "CascadingMenu",
                    },
                ],
            },
            {
                name: "maxDepth",
                editorTemplate: "DropDownField",
                defaultValue: 3,
                options: [{ value: 1 }, { value: 2 }, { value: 3 }],
                isRequired: true,
                isVisible: (item: HasFields) => item.fields.linkType === "CascadingMenu",
            },
        ],
        tab: basicTab,
    };
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            createListField(fields.primaryLinks),
            createListField(fields.secondaryLinks),
            {
                name: fields.isCompactHeader,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => false,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
